// src/hooks/useSocket.js
import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { ensureKeypair } from "../crypto/cryptoKeys";
import { registerSessionCryptoKey } from "../utils/refreshSessionData";
import { looksLikeCompactJWE, decryptCompactJwe } from "../crypto/jweDecrypt";

export default function useSocket({ marketId, mtype, type }) {
  const socketRef = useRef(null);
  const [data, setData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const reregisteringRef = useRef(false);

  useEffect(() => {
    // build once
    if (process.env.REACT_APP_MODE === "development") {
      socketRef.current = io(process.env.REACT_APP_API_URL, {
        transports: ["websocket"],
        withCredentials: true, // VERY IMPORTANT newly added for cookie handling
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500, // start after 0.5 s
        reconnectionDelayMax: 5_000, // cap at 5 s
      });
      console.log("Development Mode");
    } else {
      socketRef.current = io(process.env.REACT_APP_SOCKET_URL, {
        path: process.env.REACT_APP_SOCKET_PATH,
        withCredentials: true, // VERY IMPORTANT newly added for cookie handling
        transports: ["websocket"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500, // start after 0.5 s
        reconnectionDelayMax: 5_000, // cap at 5 s
      });
      console.log("Production Mode");
    }

    const socket = socketRef.current;
    ensureKeypair().catch(() => {});

    const reregisterCryptoKey = async () => {
      if (reregisteringRef.current) return;
      reregisteringRef.current = true;
      try {
        await registerSessionCryptoKey();
      } catch (err) {
        console.error("Socket crypto register-key failed:", err);
      } finally {
        reregisteringRef.current = false;
      }
    };

    /** --- SOCKET EVENT HANDLERS --- */
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    const onTick = async (payload) => {
      if (typeof payload === "string" && looksLikeCompactJWE(payload)) {
        try {
          payload = await decryptCompactJwe(payload);
        } catch (err) {
          console.error("Socket tick decrypt failed:", err);
          reregisterCryptoKey();
          return;
        }
      }
      setData(payload);
    };
    const onCryptoRequired = () => {
      reregisterCryptoKey();
    };
    const onError = (err) => {
      console.error("Socket error:", err.message);
      //added by uk - do not call connect() here. The client is already retrying, and a second connect was closing the socket again.
    };

    //console.log("marketId->",marketId,"mtype->",mtype,"type->",type)

    /** Re-subscribe after a reconnect so the server sends tick data again */
    const onReconnect = () => {
      console.log("Socket reconnected – re-subscribing to market data");
      setIsConnected(true);
      socket.emit("getMarketData", { marketId, mtype, type });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("tick", onTick);
    socket.on("crypto_required", onCryptoRequired);
    socket.on("connect_error", onError);
    socket.on("error", onError);
    socket.io.on("reconnect", onReconnect);
    socket.emit("getMarketData", { marketId, mtype, type });

    /** cleanup on unmount */
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("tick", onTick);
      socket.off("crypto_required", onCryptoRequired);
      socket.off("connect_error", onError);
      socket.off("error", onError);
      socket.io.off("reconnect", onReconnect);
      socket.disconnect();
    };
  }, []);

  return { data, isConnected };
}
